// src/pages/Humanitarian.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  HandHeart, 
  Users, 
  GraduationCap,
  Award,
  Smile,
  Shield,
  Sparkles,
  TrendingUp,
  DollarSign,
  Calendar,
  MapPin,
  Quote,
  Play,
  X,
  ChevronRight,
  Building2,
  Stethoscope,
  Book,
  Home,
  Utensils,
  Shirt,
  AlertCircle,
  CheckCircle2,
  Gift,
  MessageCircle,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail
} from 'lucide-react';

const Humanitarian = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [selectedStory, setSelectedStory] = useState<number | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Main Initiatives
  const initiatives = [
    {
      icon: Stethoscope,
      title: { en: 'Cancer Patient Academy', ar: 'أكاديمية مرضى السرطان' },
      description: {
        en: 'Since 2019, we have provided specialized sports programs for children battling cancer, giving them joy, strength, and hope through physical activity.',
        ar: 'منذ عام 2019، قدمنا برامج رياضية متخصصة للأطفال الذين يحاربون السرطان، ونمنحهم الفرح والقوة والأمل من خلال النشاط البدني.'
      },
      stats: { value: '150+', label: { en: 'Children Served', ar: 'طفل مستفيد' } },
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: GraduationCap,
      title: { en: 'Scholarships for Underprivileged', ar: 'منح للأطفال المحرومين' },
      description: {
        en: 'We provide free training and scholarships to talented children from low-income families, ensuring financial barriers don\'t prevent dreams.',
        ar: 'نقدم تدريباً مجانياً ومنحاً دراسية للأطفال الموهوبين من الأسر ذات الدخل المنخفض، لضمان عدم منع الحواجز المالية الأحلام.'
      },
      stats: { value: '500+', label: { en: 'Scholarships', ar: 'منحة' } },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: HandHeart,
      title: { en: 'Orphan Support Program', ar: 'برنامج دعم الأيتام' },
      description: {
        en: 'Special programs and support for orphaned children, providing them with sports training, mentorship, and a supportive community.',
        ar: 'برامج خاصة ودعم للأطفال الأيتام، وتزويدهم بالتدريب الرياضي والإرشاد ومجتمع داعم.'
      },
      stats: { value: '200+', label: { en: 'Orphans', ar: 'يتيم' } },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Users,
      title: { en: 'Community Outreach', ar: 'التواصل المجتمعي' },
      description: {
        en: 'Regular community events, free training camps, and sports equipment donations to underserved areas across Gaza.',
        ar: 'فعاليات مجتمعية منتظمة ومعسكرات تدريب مجانية وتبرعات بمعدات رياضية للمناطق المحرومة في جميع أنحاء غزة.'
      },
      stats: { value: '50+', label: { en: 'Events', ar: 'فعالية' } },
      color: 'from-green-500 to-emerald-500'
    }
  ];

  // Impact Statistics
  const impactStats = [
    { 
      icon: Heart, 
      value: '1000+', 
      label: { en: 'Lives Impacted', ar: 'حياة تأثرت' },
      color: 'text-red-500'
    },
    { 
      icon: Gift, 
      value: '$100K+', 
      label: { en: 'Aid Distributed', ar: 'مساعدات موزعة' },
      color: 'text-blue-500'
    },
    { 
      icon: Users, 
      value: '850+', 
      label: { en: 'Children Helped', ar: 'طفل ساعدناهم' },
      color: 'text-green-500'
    },
    { 
      icon: Smile, 
      value: '100%', 
      label: { en: 'Smiles Created', ar: 'ابتسامات صنعت' },
      color: 'text-yellow-500'
    },
  ];

  // Success Stories
  const successStories = [
    {
      id: 1,
      name: { en: 'Ahmed', ar: 'أحمد' },
      age: 12,
      story: {
        en: 'Battling leukemia since age 8, Ahmed found strength and joy through our cancer patient program. Today, he is cancer-free and dreams of becoming a professional footballer.',
        ar: 'يحارب سرطان الدم منذ سن 8 سنوات، وجد أحمد القوة والفرح من خلال برنامج مرضى السرطان لدينا. اليوم، هو خالٍ من السرطان ويحلم بأن يصبح لاعب كرة قدم محترف.'
      },
      image: '/placeholder.svg',
      program: { en: 'Cancer Patient Academy', ar: 'أكاديمية مرضى السرطان' }
    },
    {
      id: 2,
      name: { en: 'Fatima', ar: 'فاطمة' },
      age: 14,
      story: {
        en: 'An orphan with immense talent, Fatima received a full scholarship and is now representing Palestine in international basketball tournaments.',
        ar: 'يتيمة ذات موهبة كبيرة، حصلت فاطمة على منحة كاملة وهي الآن تمثل فلسطين في بطولات كرة السلة الدولية.'
      },
      image: '/placeholder.svg',
      program: { en: 'Orphan Support Program', ar: 'برنامج دعم الأيتام' }
    },
    {
      id: 3,
      name: { en: 'Khaled', ar: 'خالد' },
      age: 10,
      story: {
        en: 'From a low-income family, Khaled showed exceptional swimming talent. Our scholarship program helped him become a national champion.',
        ar: 'من عائلة منخفضة الدخل، أظهر خالد موهبة استثنائية في السباحة. ساعده برنامج المنح الدراسية لدينا على أن يصبح بطلاً وطنياً.'
      },
      image: '/placeholder.svg',
      program: { en: 'Scholarship Program', ar: 'برنامج المنح الدراسية' }
    },
  ];

  // Ways to Help
  const waysToHelp = [
    {
      icon: DollarSign,
      title: { en: 'Donate', ar: 'تبرع' },
      description: { en: 'Support our programs', ar: 'ادعم برامجنا' },
      action: { en: 'Donate Now', ar: 'تبرع الآن' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: HandHeart,
      title: { en: 'Sponsor a Child', ar: 'اكفل طفل' },
      description: { en: 'Change a life', ar: 'غيّر حياة' },
      action: { en: 'Sponsor', ar: 'اكفل' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      title: { en: 'Volunteer', ar: 'تطوع' },
      description: { en: 'Give your time', ar: 'أعط وقتك' },
      action: { en: 'Join Us', ar: 'انضم إلينا' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Share2,
      title: { en: 'Spread the Word', ar: 'انشر الكلمة' },
      description: { en: 'Share our mission', ar: 'شارك رسالتنا' },
      action: { en: 'Share', ar: 'شارك' },
      color: 'from-orange-500 to-red-500'
    },
  ];

  // War Impact Section Data
  const warImpact = {
    title: { en: 'Standing Strong Through Adversity', ar: 'صمود قوي في وجه الشدائد' },
    description: {
      en: 'Despite ongoing conflict and challenges, Champions Academy continues its mission to serve children and families. We have adapted our programs to provide emergency support, psychological assistance, and hope through sports.',
      ar: 'على الرغم من الصراع المستمر والتحديات، تواصل أكاديمية تشامبيونز مهمتها لخدمة الأطفال والعائلات. قمنا بتكييف برامجنا لتقديم الدعم الطارئ والمساعدة النفسية والأمل من خلال الرياضة.'
    },
    challenges: [
      { icon: Building2, text: { en: 'Damaged Facilities', ar: 'مرافق متضررة' } },
      { icon: AlertCircle, text: { en: 'Limited Resources', ar: 'موارد محدودة' } },
      { icon: Heart, text: { en: 'Psychological Support Needed', ar: 'حاجة للدعم النفسي' } },
      { icon: Shield, text: { en: 'Safety Concerns', ar: 'مخاوف أمنية' } },
    ],
    resilience: [
      { icon: CheckCircle2, text: { en: 'Continued Training Programs', ar: 'برامج تدريبية مستمرة' } },
      { icon: Heart, text: { en: 'Mental Health Support', ar: 'دعم الصحة النفسية' } },
      { icon: Users, text: { en: 'Community Unity', ar: 'وحدة المجتمع' } },
      { icon: Sparkles, text: { en: 'Hope Through Sports', ar: 'أمل من خلال الرياضة' } },
    ]
  };

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('humanitarian.title')}
        subtitle={isRTL ? 'الرياضة تشفي، تلهم، وتوحد - رسالتنا الإنسانية' : 'Sports Heals, Inspires, and Unites - Our Humanitarian Mission'}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.humanitarian') }
        ]}
      />

      {/* Mission Statement */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", duration: 0.8 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent p-1 mb-8 shadow-2xl shadow-primary/50"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Heart className="h-12 w-12 text-primary" />
              </div>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'رسالتنا الإنسانية' : 'Our Humanitarian Mission'}
            </h2>

            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              {isRTL
                ? 'في أكاديمية تشامبيونز، نؤمن أن الرياضة لديها القدرة على الشفاء والإلهام والتوحيد. برامجنا الإنسانية مكرسة لخدمة الأطفال والأسر الأكثر حاجة، وتوفير الأمل والفرح والفرص من خلال الرياضة.'
                : 'At Champions Academy, we believe sports has the power to heal, inspire, and unite. Our humanitarian programs are dedicated to serving children and families in greatest need, providing hope, joy, and opportunities through sports.'}
            </p>

            {/* Key Values */}
            <div className="flex flex-wrap justify-center gap-4 mt-12">
              {[
                { icon: Heart, text: { en: 'Compassion', ar: 'الرحمة' } },
                { icon: Smile, text: { en: 'Hope', ar: 'الأمل' } },
                { icon: HandHeart, text: { en: 'Support', ar: 'الدعم' } },
                { icon: Sparkles, text: { en: 'Empowerment', ar: 'التمكين' } },
              ].map((value, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border hover:border-primary/50 shadow-lg transition-all duration-300"
                >
                  <value.icon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">{value.text[isRTL ? 'ar' : 'en']}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <TrendingUp className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'أثرنا' : 'Our Impact'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'التغيير الحقيقي في الأرقام' : 'Real Change in Numbers'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {impactStats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <stat.icon className={`h-12 w-12 ${stat.color} mx-auto mb-4`} />
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Initiatives */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <HandHeart className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'برامجنا' : 'Our Programs'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {t('humanitarian.initiatives')}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'برامج شاملة لخدمة المجتمع وتغيير الحياة'
                : 'Comprehensive programs serving the community and changing lives'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {initiatives.map((initiative, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -12 }}
                className="group relative p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${initiative.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

                <div className="relative">
                  {/* Icon & Stats */}
                  <div className="flex items-start justify-between mb-6">
                    <div className={`p-4 rounded-2xl bg-gradient-to-br ${initiative.color} bg-opacity-10`}>
                      <initiative.icon className="h-10 w-10 text-primary" />
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold gradient-text">{initiative.stats.value}</div>
                      <div className="text-xs text-muted-foreground font-medium">
                        {initiative.stats.label[isRTL ? 'ar' : 'en']}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-primary transition-colors">
                    {initiative.title[isRTL ? 'ar' : 'en']}
                  </h3>

                  {/* Description */}
                  <p className="text-muted-foreground leading-relaxed">
                    {initiative.description[isRTL ? 'ar' : 'en']}
                  </p>

                  {/* Learn More */}
                  <Button variant="link" className="px-0 mt-6 text-primary group/btn">
                    {isRTL ? 'اعرف المزيد' : 'Learn More'}
                    <ChevronRight className={`h-4 w-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'} transition-transform group-hover/btn:translate-x-1`} />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Smile className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'قصص ملهمة' : 'Inspiring Stories'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'قصص النجاح والأمل' : 'Stories of Success and Hope'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'حياة حقيقية تغيرت من خلال برامجنا الإنسانية'
                : 'Real lives transformed through our humanitarian programs'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {successStories.map((story, idx) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                onClick={() => setSelectedStory(story.id)}
                className="group cursor-pointer"
              >
                <div className="relative rounded-3xl overflow-hidden bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500">
                  {/* Image */}
                  <div className="relative h-64 bg-muted overflow-hidden">
                    <img
                      src={story.image}
                      alt={story.name[isRTL ? 'ar' : 'en']}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                    
                    {/* Program Badge */}
                    <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-primary/90 backdrop-blur-sm border border-primary/40 shadow-lg">
                      <span className="text-xs font-semibold text-primary-foreground">
                        {story.program[isRTL ? 'ar' : 'en']}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-2xl font-bold">{story.name[isRTL ? 'ar' : 'en']}</h3>
                      <span className="text-sm text-muted-foreground">
                        {story.age} {isRTL ? 'سنة' : 'years'}
                      </span>
                    </div>

                    <p className="text-muted-foreground leading-relaxed line-clamp-4">
                      {story.story[isRTL ? 'ar' : 'en']}
                    </p>

                    <Button variant="link" className="px-0 mt-4 text-primary">
                      {isRTL ? 'اقرأ القصة كاملة' : 'Read Full Story'}
                      <ChevronRight className={`h-4 w-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* War Impact & Resilience */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {isRTL ? 'صمود وأمل' : 'Resilience & Hope'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {warImpact.title[isRTL ? 'ar' : 'en']}
              </h2>

              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {warImpact.description[isRTL ? 'ar' : 'en']}
              </p>
            </div>

            {/* Challenges & Resilience Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Challenges */}
              <div className="p-8 rounded-3xl bg-card border border-border shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <AlertCircle className="h-8 w-8 text-orange-500" />
                  <h3 className="text-2xl font-bold">
                    {isRTL ? 'التحديات' : 'Challenges'}
                  </h3>
                </div>
                <div className="space-y-4">
                  {warImpact.challenges.map((challenge, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-3 p-4 rounded-xl bg-surface-1 border border-border"
                    >
                      <challenge.icon className="h-5 w-5 text-orange-500 flex-shrink-0" />
                      <span className="text-sm font-medium">
                        {challenge.text[isRTL ? 'ar' : 'en']}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Resilience */}
              <div className="p-8 rounded-3xl bg-card border border-border shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <h3 className="text-2xl font-bold">
                    {isRTL ? 'الصمود' : 'Our Response'}
                  </h3>
                </div>
                <div className="space-y-4">
                  {warImpact.resilience.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-3 p-4 rounded-xl bg-surface-1 border border-border"
                    >
                      <item.icon className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm font-medium">
                        {item.text[isRTL ? 'ar' : 'en']}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ways to Help */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Gift className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'انضم إلينا' : 'Join Us'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'كيف يمكنك المساعدة' : 'How You Can Help'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'كل مساهمة تحدث فرقاً في حياة طفل'
                : 'Every contribution makes a difference in a child\'s life'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {waysToHelp.map((way, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -12, scale: 1.02 }}
                className="group relative"
              >
                <div className="relative p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center overflow-hidden">
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${way.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />

                  <div className="relative">
                    {/* Icon */}
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${way.color} bg-opacity-10 mb-6`}>
                      <way.icon className="h-8 w-8 text-primary" />
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold mb-2">
                      {way.title[isRTL ? 'ar' : 'en']}
                    </h3>

                    {/* Description */}
                    <p className="text-muted-foreground text-sm mb-6">
                      {way.description[isRTL ? 'ar' : 'en']}
                    </p>

                    {/* Button */}
                    <Button className="btn-glow w-full">
                      {way.action[isRTL ? 'ar' : 'en']}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Heart className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'معاً نصنع التغيير' : 'Together We Make Change'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'كن جزءًا من رسالتنا' : 'Be Part of Our Mission'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'انضم إلينا في رحلة تغيير الحياة من خلال قوة الرياضة'
                : 'Join us in the journey of changing lives through the power of sports'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/donate">
                <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
                  <Heart className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'تبرع الآن' : 'Donate Now'}
                </Button>
              </Link>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <Mail className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'اتصل بنا' : 'Contact Us'}
              </Button>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap justify-center gap-6 mt-12 pt-12 border-t border-border"
            >
              {[
                { icon: CheckCircle2, text: { en: 'Tax Deductible', ar: 'معفى من الضرائب' } },
                { icon: Shield, text: { en: 'Secure Donations', ar: 'تبرعات آمنة' } },
                { icon: TrendingUp, text: { en: '100% Impact', ar: 'تأثير 100%' } },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                  <item.icon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{item.text[isRTL ? 'ar' : 'en']}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Humanitarian;
